import { Box, IconButton, TextField, Typography } from "@mui/material";
import UserChatCard from "../component/user-chat-card";
import BotChatCard from "../component/bot-chat-card";
import { Send, Upload, UploadRounded } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import book_chunks from '../assets/book_chunks.json'
import NavBar2 from "../component/nav-bar2";
import { AppIcon } from "../component/app-icon";
import NutriAIBotIcon from '../assets/nai.png'


function NutriAi() {
    const { GoogleGenerativeAI } = require("@google/generative-ai");

    const genAI = new GoogleGenerativeAI("AIzaSyCBFcGot8LPFw27sZmMUzZCp7yClpAsaZ0");
    
    const chat_model = genAI.getGenerativeModel({ 
      model: "gemini-1.5-flash",
      systemInstruction: "You are a Nutrion Scientist trained by Dr. Udodiri Agugo. Your name is NutriAI.",
    });
  
    const embedding_model = genAI.getGenerativeModel({
      model: "text-embedding-004"
    });
  
    const chat = chat_model.startChat({
      history: [
        {
          role: "user",
          parts: [
            {
              text: `You are a Nutrition Scientist, and your task is to assist with queries based on the provided content. Follow these rules:
    
                1. **Greetings**: If the user greets you (e.g., 'Hi', 'Hello', 'Good morning'), respond naturally without using the content.
                2. **Primary Response**: If the query is related to the provided content, use information from the provided content for your response.
                3. **When Content is Irrelevant**:
                  - If the query is unrelated to the content, respond: "Sorry, I can not help you with that at the moment."
                  - However, if the question could lead to a conversation (e.g., 'Tell me more', 'Explain further'), source external information to respond helpfully.
                4. **Context Awareness**: Refer to the previous chat to understand the user's intent or clarify if necessary.
                5. **Response Formatting**:
                  - Structure responses for clarity and readability.
                  - Use:
                    - **Headings (if needed)** for different sections.
                    - **New lines** to separate thoughts clearly.
                    - **Bold text** to highlight key points.
                    - **Ordered lists (1, 2, 3...)** for step-by-step instructions.
                    - **Unordered lists (-, *)** for general points.
                    - **Tables (if applicable)** for summarizing data.
                    - Keep responses **concise but informative**.
                
                Remember: Always prioritize being helpful, concise, and contextually relevant. Also, this is a nutritional chat—nothing else.`
            }
          ]
        },
        {
          role: "model",
          parts: [
            {
              text: `**Hey, I'm NutriAI!**  
                Trained primarily on my founder's *Nutrition Science Handbook*, I am here to assist you with nutrition-related questions.  
                
                **How I Can Help You**
                - Provide evidence-based nutritional advice.  
                - Answer questions about food, diet, and health.  
                - Explain concepts in an easy-to-understand way.  
                
                Ask me anything related to nutrition, and I'll be happy to assist!`
            }
          ]
        }
      ]
    });
    
    
    const [bookChunks, setBookChunks] = useState([]);
  
    const [messages, setMessages] = useState([
      {
        author: 1,
        content: `**Hey, I'm NutriAI!**  
          Trained primarily on my founder's *Nutrition Science Pamphlet: **Personalized Nutrition: Way forward: for healthy living** which is just an extension of her original textbook: **Handbook of Nutrition and DIetetics.** 
          I am here to assist you with nutrition-related questions.
          Ask me anything related to nutrition, and I'll be happy to assist!`
      }
    ]);
  
    const [message, setMessage] = useState('');
  
    const messagesEndRef = useRef(null);
  
    useEffect(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, [messages]);
  
    useEffect(() => {
      setBookChunks(book_chunks);
    }, []);
  
    async function embedPrompt(text) {
      const embedded_prompt = await embedding_model.embedContent(text);
      return embedded_prompt.embedding.values;
    }
    
    function calculate_magnitude(vector) {
      let total_sum = 0;
  
      for (let index = 0; index < vector.length; index++) {
        total_sum += Math.pow(vector[index], 2);
      }
  
      return Math.sqrt(total_sum);
    }
  
    function similar_chunks(embedded_prompt) {    
      let n = bookChunks.length, m = bookChunks[0]['vector'].length;
  
      // compute the dot products
      let dot_products = [];
  
      for (let i = 0; i < n; i++) {
        let curProduct = 0;
        for (let j = 0; j < m; j++) {
          curProduct += embedded_prompt[j] * bookChunks[i]['vector'][j];
        }
        dot_products.push(curProduct)
      }
  
      // compute magnitudes
      let magnitudes = [];
      
      const user_magnitude = calculate_magnitude(embedded_prompt);
  
      for (let i = 0; i < n; i++) {
        magnitudes.push(user_magnitude * calculate_magnitude(bookChunks[i]['vector']))
      }
      
      
      // finally calculate cosine similarity; look for similar chunks using 0.5 as threshold
      let relevant_chunks = [];
  
      for (let i = 0; i < n; i++) {
        // console.log(dot_products[i] / magnitudes[i]);
        
        if (dot_products[i] / magnitudes[i] >= 0.5) {
          relevant_chunks.push(bookChunks[i]['content']);
        }
      }
      console.log(relevant_chunks);
      return relevant_chunks.length > 0 ? relevant_chunks.join("\n\n") : "No relevant content found.";
    }
  
    const sendMessage = async () => {
      if (message === '') {
        return;
      }
  
      // 0 represents the human and 1 reps. the bot
      const newMessage = { author: 0, content: message };
      setMessages((prevMessages) => [...prevMessages, newMessage]);
      
      const message_embedding = await embedPrompt(message);
  
      setMessage('');
      
      const similar_contents = similar_chunks(message_embedding);
  
      const result = await chat.sendMessage(`user question: ${message}. \n content: ${similar_contents}`);
  
      const botMessage = { author: 1, content: result.response.text() };
      setMessages((prevMessages) => [...prevMessages, botMessage]);
    }
  
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100vh",
        }}
      >
        <NavBar2 svgLogo={<img src={NutriAIBotIcon}/>}/>
      {/* Chat Box Container */}
      <Box
        sx={{
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'flex-end', 
          alignItems: 'center', // Center horizontally
          width: '100%', // Ensure it takes full width
        }}
      >
        {/* Chat Messages */}
        <Box
          sx={{
            maxWidth: '800px', 
            width: '100%', // Ensure it takes full width
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'flex-end', 
          }}
        >
          {messages.map((data, index) =>
            data.author === 0 ? (
              <UserChatCard key={index} message={data.content} />
            ) : (
              <BotChatCard key={index} message={data.content} />
            )
          )}
          {/* Invisible element to ensure auto-scroll */}
          <div ref={messagesEndRef} />
        </Box>
      </Box>
  
      {/* Input Box */}
      <Box
        sx={{
          backgroundColor: "white",
          position: "sticky",
          bottom: 0,
          padding: 1.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center", // Centers input & button vertically
            gap: 1,
            backgroundColor: "#cdced4",
            borderRadius: 5,
            width: {xs: "90%", md: "100%"},
            maxWidth: "600px",
            padding: "8px 12px", // Added padding for better alignment
          }}
        >
          <TextField
            variant="standard"
            placeholder="Type your message..."
            onChange={(e) => setMessage(e.target.value)}
            value={message}
            multiline
            InputProps={{
              disableUnderline: true,
              style: {
                padding: "8px 0px",
                maxHeight: "120px",
                overflow: "auto",
              },
            }}
            sx={{
              flex: 1,
              resize: "none",
            }}
          />
          
          {/* Show send button only if message is not empty */}
          {message.trim() && (
            <IconButton
              sx={{
                color: "#fff",
                backgroundColor: "#000000",
                margin: 1,
              }}
              onClick={sendMessage}
            >
              <UploadRounded />
            </IconButton>
          )}
        </Box>
  
        {/* NutriAI development notice */}
        <Typography sx={{ color: "gray", fontSize: "12px" }}>
          NutriAI is still in development,{" "}
          <a href="#" style={{ textDecoration: "underline", color: "inherit" }}>
            Learn More
          </a>.
        </Typography>
      </Box>
  
      </Box>
    );  
};
export default NutriAi;